import { logger, LogCategory } from './logger';

export interface QuikQAuthCredentials {
  userId: string;
  password: string;
  startCode: string; // Required but can be provided with default value
}

export interface QuikQAuthResponse {
  status: string;
  message: string;
  data: {
    token: string;
  };
}

export interface QuikQUser {
  userId: string;
  token: string;
  startCode?: string; // Added for API calls
  accountCode?: string; // Added for API calls
  role: 'USER' | 'DRIVER'; // Support for different user types
}

// New interfaces for password reset functionality
export interface QuikQForgotPasswordRequest {
  startCode: string; // Required but can be provided with default value
  userId: string;
}

export interface QuikQResetPasswordRequest {
  startCode: string;
  userId: string;
  oldPassword: string;
  newPassword: string;
}

export interface QuikQResetUUIDRequest {
  startCode: string;
  resetUuid: string;
  newPassword: string;
}

export interface QuikQTwoFactorRequest {
  twoFactorCode: string;
  rememberDevice?: boolean;
}

class QuikQAuthService {
  private readonly API_BASE_URL = 'https://api.quikq.com/v1';
  private readonly AUTH_ENDPOINT = '/auth';
  private readonly TOKEN_KEY = 'quikq_token';
  private readonly USER_KEY = 'quikq_user';

  // Proactive token-refresh in the background so the session doesn't silently die
  // after the QuikQ JWT TTL (~30 min) while the user is mid-task.
  private readonly REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
  private readonly REFRESH_MIN_GAP_MS = 60 * 1000; // never refresh more than once per minute
  private readonly VISIBILITY_REFRESH_AFTER_MS = 5 * 60 * 1000; // when tab returns to foreground after > 5 min
  private refreshTimerId: ReturnType<typeof setInterval> | null = null;
  private lastRefreshAt = 0;
  private inflightRefresh: Promise<string | null> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private lastHiddenAt: number | null = null;

  // Header names QuikQ has been observed to use for sliding-window refreshes.
  private readonly REFRESHED_TOKEN_HEADERS = [
    'RefreshedAuthToken',
    'refreshedauthtoken',
    'X-Refreshed-Token',
    'x-refreshed-token',
  ];

  // Normalize userId when it's an email address (case-insensitive in practice)
  private normalizeUserId(userId: string): string {
    const trimmed = (userId || '').trim();
    return trimmed.includes('@') ? trimmed.toLowerCase() : trimmed;
  }

  // Decode JWT token to extract payload information
  private decodeJWTToken(token: string): any {
    try {
      // JWT token has 3 parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        logger.warn(LogCategory.AUTH, 'Invalid JWT token format');
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decodedPayload = atob(paddedPayload);
      
      // console.log('🔍 Decoded JWT payload:', decodedPayload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      logger.error(LogCategory.AUTH, 'Error decoding JWT token', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  // Login
  async login(credentials: QuikQAuthCredentials): Promise<QuikQUser> {
    /*
     * QuikQ API Error Codes Reference:
     * - Error Code -2: Authentication failed - Invalid credentials
     * - Error Code -4: Invalid Start Code format
     * - Error Code -6: Account locked or suspended
     * - Error Code -8: Too many failed attempts
     * - Error Code -10: Account expired
     */
    try {
      const normalizedUserId = this.normalizeUserId(credentials.userId);
      const normalizedStartCode = (credentials.startCode || '').trim();

      // Validate required fields
      if (!normalizedUserId || !credentials.password || !normalizedStartCode) {
        throw new Error('Missing required fields: userId, password, and startCode are required');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      // Log the request data for debugging
      // console.log('🔍 Login request data:', {
      //   userId: credentials.userId,
      //   password: credentials.password ? '***' : 'missing',
      //   startCode: credentials.startCode,
      //   hasPassword: !!credentials.password
      // });

      // Additional debugging information
      // console.log('🔍 API Base URL:', this.API_BASE_URL);
      // console.log('🔍 Request URL:', `${this.API_BASE_URL}/auth`);
      // console.log('🔍 User ID length:', credentials.userId.length);
      // console.log('🔍 Password length:', credentials.password.length);
      // console.log('🔍 Start Code length:', credentials.startCode.length);
      // console.log('🔍 Start Code format check:', {
      //   isNumeric: /^\d+$/.test(credentials.startCode),
      //   hasLeadingZeros: credentials.startCode.startsWith('0'),
      //   length: credentials.startCode.length
      // });

      // Try different startCode formats based on error code -4
      // Error code -4 might indicate startCode format issue
      // QuikQ API expects startCode as integer, not string
      const alternativeCredentials: any = {
        userId: normalizedUserId,
        password: credentials.password,
        startCode: parseInt(normalizedStartCode, 10) // Convert to integer
      };
      
      // console.log('🔍 Converted startCode to integer:', alternativeCredentials.startCode, typeof alternativeCredentials.startCode);

      // Log the actual request body (without password)
      // const requestBody = JSON.stringify(alternativeCredentials);
      // console.log('🔍 Request body (without password):', requestBody.replace(credentials.password, '***'));

      // Try with JSON format first using alternative credentials (padded startCode if needed)
      let response;
      try {
        response = await fetch(`${this.API_BASE_URL}${this.AUTH_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'User-Agent': 'NorthStar-Fuel-App/1.0',
          },
          body: JSON.stringify(alternativeCredentials),
          signal: controller.signal,
        });
      } catch (fetchError) {
        console.error('🔍 Fetch error:', fetchError);
        if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
          throw new Error('CORS error or network issue. Please check your internet connection or try again later.');
        }
        throw fetchError;
      }

      clearTimeout(timeoutId);

      // Log response details for debugging
      // console.log('🔍 Login response status:', response.status);
      // console.log('🔍 Login response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Try to get error details from response body
        let errorDetails = '';
        let errorDataParsed = null;
        try {
          const errorText = await response.text();
          errorDetails = errorText;
          // Try to parse as JSON
          try {
            errorDataParsed = JSON.parse(errorText);
          } catch {
            // Not JSON, keep as text
          }
        } catch (e) {
          // console.log('🔍 Could not read error response body');
        }
        
        // Handle specific HTTP status codes
        if (response.status === 404) {
          throw new Error('API endpoint not found. Please check the API configuration or contact support.');
        }
        if (response.status === 403) {
          // 403 is often geolocation restriction - always log with SECURITY category
          const errorText = errorDetails.toLowerCase();
          const isGeolocationBlock = errorText.includes('country') || 
                                      errorText.includes('region') || 
                                      errorText.includes('location') ||
                                      errorText.includes('geolocation') ||
                                      errorText.includes('geographic') ||
                                      errorText.includes('not available') ||
                                      errorText.includes('unavailable in your region') ||
                                      errorText.includes('outside') ||
                                      errorText.includes('us only') ||
                                      errorText.includes('usa only') ||
                                      errorText.includes('united states only');
          
          // Get response headers for additional info
          const responseHeaders: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            responseHeaders[key] = value;
          });
          
          // Always log 403 as SECURITY event with detailed info
          const logMessage = isGeolocationBlock 
            ? 'Login blocked: HTTP 403 - Geolocation restriction detected (User outside USA)'
            : 'Login blocked: HTTP 403 Forbidden - Possible geolocation restriction or access denied';
          
          logger.error(LogCategory.SECURITY, logMessage, new Error(`HTTP 403 Forbidden`), {
            userId: normalizedUserId,
            statusCode: response.status,
            httpStatus: 403,
            errorDetails: errorDetails.substring(0, 500),
            errorData: errorDataParsed,
            responseHeaders: responseHeaders,
            reason: isGeolocationBlock ? 'geolocation_restriction_confirmed' : 'access_forbidden_403',
            note: 'HTTP 403 often indicates geographic restrictions. Check errorDetails and responseHeaders for more info.',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
          });
          
          if (isGeolocationBlock) {
            throw new Error('This service is only available in the United States. Access from your current location is not permitted.');
          }
          
          throw new Error('Access forbidden. Please check your credentials or contact support.');
        }
        if (response.status === 500) {
          throw new Error('Server error. Please try again later or contact support.');
        }
        if (response.status === 0) {
          throw new Error('CORS error or network issue. Please check your internet connection or try again later.');
        }
        
        // Check for geolocation/region restriction in error details (for non-403 errors)
        const errorText = errorDetails.toLowerCase();
        const isGeolocationBlock = errorText.includes('country') || 
                                    errorText.includes('region') || 
                                    errorText.includes('location') ||
                                    errorText.includes('geolocation') ||
                                    errorText.includes('geographic') ||
                                    errorText.includes('not available') ||
                                    errorText.includes('unavailable in your region') ||
                                    errorText.includes('outside') ||
                                    errorText.includes('us only') ||
                                    errorText.includes('usa only') ||
                                    errorText.includes('united states only');
        
        if (isGeolocationBlock) {
          // Log geolocation blocking with SECURITY category
          logger.error(LogCategory.SECURITY, 'Login blocked: User outside USA/restricted region detected in error response', new Error(`Geolocation restriction: ${errorDetails.substring(0, 200)}`), {
            userId: normalizedUserId,
            statusCode: response.status,
            errorDetails: errorDetails.substring(0, 500),
            errorData: errorDataParsed,
            reason: 'geolocation_restriction_confirmed',
            httpStatus: response.status
          });
          throw new Error('This service is only available in the United States. Access from your current location is not permitted.');
        }
        
        // Handle specific error codes
        if (errorDetails.includes('error code:: -2')) {
          // console.log('🔍 Error code -2 detected: Authentication failed');
          throw new Error('Invalid credentials. Please check your User ID, Password, and Start Code and try again.');
        }
        
        if (errorDetails.includes('error code:: -4')) {
          // console.log('🔍 Error code -4 detected: Invalid Start Code format');
          throw new Error('Invalid Start Code format. Please check your Start Code and try again.');
        }
        
        if (errorDetails.includes('error code:: -6')) {
          // console.log('🔍 Error code -6 detected: Account locked or suspended');
          throw new Error('Account is locked or suspended. Please contact support for assistance.');
        }
        
        if (errorDetails.includes('error code:: -8')) {
          // console.log('🔍 Error code -8 detected: Too many failed attempts');
          throw new Error('Too many failed login attempts. Please wait before trying again or contact support.');
        }
        
        if (errorDetails.includes('error code:: -10')) {
          // console.log('🔍 Error code -10 detected: Account expired');
          throw new Error('Account has expired. Please contact support for assistance.');
        }
        
        // If first attempt fails with error code -4, try alternative formats
        if (errorDetails.includes('error code:: -4')) {
          // console.log('🔍 Error code -4 detected, trying alternative formats...');
          
          // Try with different field names
          const altCredentials2 = {
            username: normalizedUserId,  // Try 'username' instead of 'userId'
            password: credentials.password,
            code: alternativeCredentials.startCode  // Try 'code' instead of 'startCode'
          };
          
          // console.log('🔍 Trying alternative field names:', {
          //   username: altCredentials2.username,
          //   password: '***',
          //   code: altCredentials2.code
          // });
          
          let response2;
          try {
            response2 = await fetch(`${this.API_BASE_URL}${this.AUTH_ENDPOINT}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'User-Agent': 'NorthStar-Fuel-App/1.0',
              },
              body: JSON.stringify(altCredentials2),
              signal: controller.signal,
            });
          } catch (fetchError) {
            console.error('🔍 Fetch error (alternative format):', fetchError);
            if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
              throw new Error('CORS error or network issue. Please check your internet connection or try again later.');
            }
            throw fetchError;
          }
          
          clearTimeout(timeoutId);
          
          if (response2.ok) {
            const data2: QuikQAuthResponse = await response2.json();
            if (data2.status === 'success') {
              // Success with alternative format, continue with token processing
              const tokenPayload = this.decodeJWTToken(data2.data.token);
              const extractedRole = tokenPayload?.roles || tokenPayload?.role || 'USER';
              
              // Determine user type based on JWT payload or user ID pattern
              let userRole: 'USER' | 'DRIVER' = 'USER';
              
              if (extractedRole === 'DRIVER' || 
                  extractedRole === 'driver' || 
                  normalizedUserId.toLowerCase().includes('driver') ||
                  tokenPayload?.userType === 'DRIVER' ||
                  tokenPayload?.driverType) {
                userRole = 'DRIVER';
              }
              
              // console.log('🔍 Extracted role from JWT:', extractedRole);
              // console.log('🔍 Determined user role:', userRole);

              const user: QuikQUser = {
                userId: normalizedUserId,
                token: data2.data.token,
                startCode: alternativeCredentials.startCode,
                role: userRole,
              };

              // console.log('🔍 Created user object (alternative format):', {
              //   userId: user.userId,
              //   startCode: user.startCode,
              //   role: user.role,
              //   tokenLength: user.token.length
              // });

              this.saveUser(user);
              return user;
            }
          }
        }
        
        // Try form data format as fallback
        // console.log('🔍 Trying form data format...');
        const formData = new FormData();
        formData.append('userId', normalizedUserId);
        formData.append('password', credentials.password);
        formData.append('startCode', normalizedStartCode);
        
        let response3;
        try {
          response3 = await fetch(`${this.API_BASE_URL}${this.AUTH_ENDPOINT}`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'User-Agent': 'NorthStar-Fuel-App/1.0',
            },
            body: formData,
            signal: controller.signal,
          });
        } catch (fetchError) {
          console.error('🔍 Fetch error (form data):', fetchError);
          if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
            throw new Error('CORS error or network issue. Please check your internet connection or try again later.');
          }
          throw fetchError;
        }
        
        clearTimeout(timeoutId);
        
        if (response3.ok) {
          const data3: QuikQAuthResponse = await response3.json();
          if (data3.status === 'success') {
            const tokenPayload = this.decodeJWTToken(data3.data.token);
            const extractedRole = tokenPayload?.roles || tokenPayload?.role || 'USER';
            
            // Determine user type based on JWT payload or user ID pattern
            let userRole: 'USER' | 'DRIVER' = 'USER';
            
            if (extractedRole === 'DRIVER' || 
                extractedRole === 'driver' || 
                normalizedUserId.toLowerCase().includes('driver') ||
                tokenPayload?.userType === 'DRIVER' ||
                tokenPayload?.driverType) {
              userRole = 'DRIVER';
            }
            
            // console.log('🔍 Extracted role from JWT:', extractedRole);
            // console.log('🔍 Determined user role:', userRole);

            const user: QuikQUser = {
              userId: normalizedUserId,
              token: data3.data.token,
              startCode: normalizedStartCode,
              role: userRole,
            };

            // console.log('🔍 Created user object (form data):', {
            //   userId: user.userId,
            //   startCode: user.startCode,
            //   role: user.role,
            //   tokenLength: user.token.length
            // });

            this.saveUser(user);
            return user;
          }
        }
        
        // If all attempts fail, provide a more helpful error message
        // Note: If we got here, response.status is not 403 (403 is handled above)
        if (errorDetails.includes('error code:: -2')) {
          throw new Error('Authentication failed. Please verify your User ID, Password, and Start Code are correct.');
        } else if (errorDetails.includes('error code:: -4')) {
          throw new Error('Invalid Start Code format. Please check your Start Code and try again.');
        } else {
          // Check if it's a CORS or network issue
          if (response.status === 0 || errorDetails.includes('CORS') || errorDetails.includes('NetworkError')) {
            throw new Error('CORS error or network issue. Please check your internet connection or try again later.');
          }
          // Create error with status info for better logging
          const errorMsg = `Login failed: ${response.status} ${response.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`;
          const loginError = new Error(errorMsg);
          (loginError as any).httpStatus = response.status; // Attach HTTP status for logging
          throw loginError;
        }
      }

      const data: QuikQAuthResponse = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Authentication failed');
      }

      // Decode JWT token to extract role and other information
      const tokenPayload = this.decodeJWTToken(data.data.token);
      const extractedRole = tokenPayload?.roles || tokenPayload?.role || 'USER';
      
      // Determine user type based on JWT payload or user ID pattern
      let userRole: 'USER' | 'DRIVER' = 'USER';
      
      // Check if user is a driver based on JWT payload or user ID pattern
      if (extractedRole === 'DRIVER' || 
          extractedRole === 'driver' || 
          normalizedUserId.toLowerCase().includes('driver') ||
          tokenPayload?.userType === 'DRIVER' ||
          tokenPayload?.driverType) {
        userRole = 'DRIVER';
      }
      
      // console.log('🔍 Extracted role from JWT:', extractedRole);
      // console.log('🔍 Determined user role:', userRole);

      const user: QuikQUser = {
        userId: normalizedUserId,
        token: data.data.token,
        startCode: normalizedStartCode,
        role: userRole,
      };

      // console.log('🔍 Created user object:', {
      //   userId: user.userId,
      //   startCode: user.startCode,
      //   role: user.role,
      //   tokenLength: user.token.length
      // });

      // Save to localStorage
      this.saveUser(user);
      
      // Log successful login
      logger.logLogin(user.userId, true);
      
      return user;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      const errorMessage = errorObj.message.toLowerCase();
      const errorStack = errorObj.stack || '';
      const httpStatus = (errorObj as any).httpStatus; // Check if HTTP status is attached
      
      // Check if this is a geolocation restriction error
      const isGeolocationError = errorMessage.includes('united states') || 
                                  errorMessage.includes('not available') ||
                                  errorMessage.includes('geolocation') ||
                                  errorMessage.includes('region') ||
                                  errorMessage.includes('access forbidden') ||
                                  errorStack.includes('geolocation');
      
      // Check if it's a 403 error (might be geolocation)
      const is403Error = httpStatus === 403 || errorMessage.includes('403') || errorMessage.includes('forbidden');
      
      // Log login attempt (failed)
      logger.logLogin(this.normalizeUserId(credentials.userId), false, errorObj);
      
      // Log with appropriate category - use SECURITY for geolocation or 403 errors
      if (isGeolocationError || is403Error) {
        const logMsg = is403Error 
          ? 'Login blocked: HTTP 403 Forbidden - Possible geolocation restriction' 
          : 'Login blocked: Geolocation restriction detected';
        
        logger.error(LogCategory.SECURITY, logMsg, errorObj, {
          userId: this.normalizeUserId(credentials.userId),
          errorCode: errorObj.message,
          errorName: errorObj.name,
          httpStatus: httpStatus || (is403Error ? 403 : undefined),
          reason: isGeolocationError ? 'geolocation_restriction' : 'possible_geolocation_restriction_403',
          fullError: errorObj.toString(),
          note: is403Error ? 'HTTP 403 often indicates geographic restrictions or access denied' : undefined
        });
      } else {
        logger.error(LogCategory.AUTH, 'QuikQ Auth Error', errorObj, {
          userId: this.normalizeUserId(credentials.userId),
          errorCode: errorObj.message,
          errorName: errorObj.name,
          httpStatus: httpStatus
        });
      }
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timed out. Please check your internet connection and try again.');
        }
        if (error.message.includes('Failed to fetch')) {
          throw new Error('Unable to connect to QuikQ API. Please check your internet connection or try again later.');
        }
        if (error.message.includes('CORS')) {
          throw new Error('CORS error. Please contact support.');
        }
        if (error.message.includes('NetworkError')) {
          throw new Error('Network error. Please check your internet connection and try again.');
        }
        if (error.message.includes('TypeError')) {
          throw new Error('API communication error. Please try again or contact support.');
        }
      }
      
      throw error;
    }
  }

  // Logout
  logout(): void {
    const user = this.getCurrentUser();
    const userId = user?.userId;

    this.stopTokenRefreshTimer();
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    // Drop persisted station/route cache so the next user on this device
    // does not see the previous session's QuikQ repricing data.
    void import('./stationsCache').then((m) => m.clearStationsCache());

    // Log logout
    logger.logLogout(userId);
  }

  // Get current user
  getCurrentUser(): QuikQUser | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) {
      // console.log('🔍 No user data in localStorage');
      return null;
    }
    
    try {
      const user = JSON.parse(userStr) as QuikQUser;
      const tokenFromStorage = localStorage.getItem(this.TOKEN_KEY);
      if (tokenFromStorage && user.token !== tokenFromStorage) {
        // Keep USER_KEY in sync with the canonical token storage.
        user.token = tokenFromStorage;
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));
      }
      // console.log('🔍 Retrieved user from localStorage:', {
      //   userId: user.userId,
      //   startCode: user.startCode,
      //   role: user.role,
      //   hasToken: !!user.token
      // });
      return user;
    } catch (error) {
      logger.error(LogCategory.AUTH, 'Error parsing user from localStorage', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  // Get current user role
  getCurrentUserRole(): string | null {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  // Check if current user is a driver
  isDriver(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'DRIVER';
  }

  // Check if current user is a regular user (not driver)
  isUser(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'USER';
  }

  // Get auth token
  getToken(): string | null {
    const token = localStorage.getItem(this.TOKEN_KEY);
    // console.log('🔍 Reading token from localStorage:', token ? 'present' : 'missing');
    // if (token) {
    //   console.log('🔍 Token length:', token.length);
    //   console.log('🔍 Token starts with:', token.substring(0, 20) + '...');
    // }
    return token;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Save user to localStorage
  private saveUser(user: QuikQUser): void {
    // console.log('💾 Saving user to localStorage:', {
    //   userId: user.userId,
    //   tokenLength: user.token.length,
    //   startCode: user.startCode,
    //   role: user.role
    // });
    
    localStorage.setItem(this.TOKEN_KEY, user.token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    
    // console.log('✅ User saved to localStorage');
    // console.log('🔍 Token in localStorage:', localStorage.getItem(this.TOKEN_KEY) ? 'present' : 'missing');
    
    // Verify that startCode was saved correctly
    const savedUserStr = localStorage.getItem(this.USER_KEY);
    if (savedUserStr) {
      try {
        const savedUser = JSON.parse(savedUserStr);
        // console.log('🔍 Verified saved user:', {
        //   userId: savedUser.userId,
        //   startCode: savedUser.startCode,
        //   role: savedUser.role
        // });
      } catch (error) {
        logger.error(LogCategory.AUTH, 'Error parsing saved user', error instanceof Error ? error : new Error(String(error)));
      }
    }

    // Make sure the background refresh loop is running so the session doesn't
    // silently die at the QuikQ JWT TTL while the user is mid-task.
    this.startTokenRefreshTimer();
  }

  /**
   * Look for a refreshed JWT on any authenticated API response and persist it.
   * QuikQ rotates the session token via a response header on most authenticated
   * endpoints; if we ignore it, the original token will expire at its hard TTL
   * (~30 min) regardless of user activity.
   */
  consumeRefreshedAuthToken(response: Response | null | undefined): boolean {
    if (!response || typeof response.headers?.get !== 'function') return false;

    let refreshedToken: string | null = null;
    for (const name of this.REFRESHED_TOKEN_HEADERS) {
      const value = response.headers.get(name);
      if (value && value.trim()) {
        refreshedToken = value.trim();
        break;
      }
    }

    if (!refreshedToken) return false;

    const currentToken = this.getToken();
    if (currentToken === refreshedToken) return false;

    try {
      const user = this.getCurrentUser();
      if (user) {
        user.token = refreshedToken;
        this.saveUser(user);
      } else {
        localStorage.setItem(this.TOKEN_KEY, refreshedToken);
      }
      this.lastRefreshAt = Date.now();
      return true;
    } catch (error) {
      logger.warn(LogCategory.AUTH, 'Failed to persist refreshed auth token', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Start a background timer that keeps the QuikQ session alive while the
   * tab/app is open. Safe to call multiple times — it'll just no-op if a
   * timer is already running.
   */
  startTokenRefreshTimer(): void {
    if (typeof window === 'undefined') return;
    if (!this.isAuthenticated()) return;

    if (this.refreshTimerId == null) {
      this.refreshTimerId = setInterval(() => {
        this.runBackgroundRefresh('interval');
      }, this.REFRESH_INTERVAL_MS);
    }

    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (typeof document === 'undefined') return;
        if (document.visibilityState === 'hidden') {
          this.lastHiddenAt = Date.now();
          return;
        }
        if (document.visibilityState === 'visible') {
          const hiddenFor = this.lastHiddenAt ? Date.now() - this.lastHiddenAt : 0;
          this.lastHiddenAt = null;
          // If we were backgrounded for a while, browsers may have throttled the
          // interval timer — proactively refresh on resume.
          if (hiddenFor >= this.VISIBILITY_REFRESH_AFTER_MS || hiddenFor === 0) {
            this.runBackgroundRefresh('visibility');
          }
        }
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this.visibilityHandler);
      }
    }
  }

  /** Tear down the background refresh timer (call on logout). */
  stopTokenRefreshTimer(): void {
    if (this.refreshTimerId != null) {
      clearInterval(this.refreshTimerId);
      this.refreshTimerId = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    this.lastHiddenAt = null;
  }

  private async runBackgroundRefresh(reason: 'interval' | 'visibility'): Promise<void> {
    if (!this.isAuthenticated()) {
      this.stopTokenRefreshTimer();
      return;
    }

    const now = Date.now();
    if (now - this.lastRefreshAt < this.REFRESH_MIN_GAP_MS) {
      return;
    }

    if (this.inflightRefresh) {
      try {
        await this.inflightRefresh;
      } catch {
        // refreshToken handles its own errors; ignore here.
      }
      return;
    }

    this.inflightRefresh = this.refreshToken().catch((error) => {
      logger.warn(LogCategory.AUTH, `Background token refresh failed (${reason})`, {
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    });

    try {
      const result = await this.inflightRefresh;
      if (result) {
        this.lastRefreshAt = Date.now();
      }
    } finally {
      this.inflightRefresh = null;
    }
  }

  // Refresh token if needed (for future use)
  async refreshToken(): Promise<string | null> {
    const user = this.getCurrentUser();
    if (!user) return null;

    try {
      const currentToken = this.getToken() || user.token;
      if (!currentToken) {
        this.logout();
        return null;
      }

      // Make a simple API call to check if we get a refreshed token
      const response = await fetch(`${this.API_BASE_URL}/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });

      // Persist sliding-refresh token if QuikQ returned one in the response headers.
      if (this.consumeRefreshedAuthToken(response)) {
        return this.getToken();
      }

      // If no refreshed token, check if current token is still valid
      if (response.ok) {
        // Ensure both token stores stay aligned even when API does not return a new token.
        if (user.token !== currentToken) {
          user.token = currentToken;
          this.saveUser(user);
        }
        return currentToken; // Current token is still valid
      } else if (response.status === 401) {
        logger.warn(LogCategory.AUTH, 'Current token is invalid, user needs to re-authenticate', { userId: user.userId });
        this.logout();
        return null;
      }

      return currentToken;
    } catch (error) {
      logger.error(LogCategory.AUTH, 'Error refreshing token', error instanceof Error ? error : new Error(String(error)), { userId: user.userId });
      // If refresh fails, return current token (let the API call fail naturally)
      return this.getToken() || user.token;
    }
  }

  // Get auth headers for API requests (JSON payload requests)
  async getAuthHeaders(): Promise<Record<string, string>> {
    return this.getJsonAuthHeaders();
  }

  // Get auth headers for JSON requests (POST/PUT/PATCH)
  async getJsonAuthHeaders(): Promise<Record<string, string>> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // If token is expired, try to refresh it (QuikQ may return a new token via /auth/validate).
    if (this.isTokenExpired(token)) {
      try {
        const refreshedToken = await this.refreshToken();
        if (!refreshedToken) {
          logger.warn(LogCategory.AUTH, 'Token is expired and refresh failed, user should re-authenticate');
          throw new Error('Authentication token has expired. Please log in again.');
        }

        return {
          'Authorization': `Bearer ${refreshedToken}`,
          'Content-Type': 'application/json',
        };
      } catch (e) {
        // refreshToken() may already logout on 401; ensure we throw a consistent error
        this.logout();
        throw e instanceof Error ? e : new Error('Authentication token has expired. Please log in again.');
      }
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Get auth headers for GET requests (avoid unnecessary Content-Type)
  async getGetAuthHeaders(): Promise<Record<string, string>> {
    const jsonHeaders = await this.getJsonAuthHeaders();
    const { ['Content-Type']: _contentType, ...getHeaders } = jsonHeaders;
    return getHeaders;
  }

  // Check if token is expired
  private isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeJWTToken(token);
      if (!decoded || !decoded.exp) {
        return true; // Consider invalid tokens as expired
      }
      
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenExpiry = decoded.exp;
      
      // Add 5 minute buffer to prevent edge cases
      return currentTime >= (tokenExpiry - 300);
    } catch (error) {
      logger.error(LogCategory.AUTH, 'Error checking token expiration', error instanceof Error ? error : new Error(String(error)));
      return true; // Consider tokens with errors as expired
    }
  }

  // Forgot Password
  async forgotPassword(request: QuikQForgotPasswordRequest): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${this.API_BASE_URL}/auth/forgotPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to send reset email: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to send reset email');
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logger.error(LogCategory.AUTH, 'QuikQ Forgot Password Error', errorObj, { startCode: request.startCode, userId: request.userId });
      throw error;
    }
  }

  // Reset Password (with old password)
  async resetPassword(request: QuikQResetPasswordRequest): Promise<void> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/auth/resetPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Reset password failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Reset password failed');
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logger.error(LogCategory.AUTH, 'QuikQ Reset Password Error', errorObj, { userId: request.userId });
      throw error;
    }
  }

  // Reset Password with UUID (from email link)
  async resetPasswordWithUUID(request: QuikQResetUUIDRequest): Promise<void> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/auth/resetUUID`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Reset password with UUID failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Reset password with UUID failed');
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logger.error(LogCategory.AUTH, 'QuikQ Reset Password with UUID Error', errorObj);
      throw error;
    }
  }

  // Two-Factor Authentication (if needed in future)
  async verifyTwoFactor(request: QuikQTwoFactorRequest): Promise<QuikQUser> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`2FA verification failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || '2FA verification failed');
      }

      // Update user with 2FA verification
      const user = this.getCurrentUser();
      if (user) {
        user.token = data.data.token;
        this.saveUser(user);
      }

      return user!;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logger.error(LogCategory.AUTH, 'QuikQ 2FA Error', errorObj);
      throw error;
    }
  }

  // Set token duration preference
  setTokenDuration(durationInMinutes: number): void {
    localStorage.setItem('tokenDuration', durationInMinutes.toString());
    // console.log(`🔐 Token duration set to ${durationInMinutes} minutes`);
  }

  // Get token duration preference
  getTokenDuration(): number {
    const saved = localStorage.getItem('tokenDuration');
    return saved ? parseInt(saved) : 60; // Default to 60 minutes
  }
}

// Export singleton instance
export const quikqAuthService = new QuikQAuthService();